/**
 * Reuniones — listado global del comercial (Paso 3 spec v3.2.A).
 *
 * Pantalla global (todas las reuniones del usuario) con filtros + KPIs.
 * Endpoint: GET /api/reuniones (ya filtra por responsable_comercial).
 *
 * Desde aquí el comercial:
 *  - Continúa una reunión en curso → ReunionConsultiva.
 *  - Abre el Informe interno de una reunión completada → InformeConsultivo.
 */
import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Search, FileText, Play, Compass, Users,
  CheckCircle2, Clock, CircleDashed, TrendingUp,
} from "lucide-react";
import ReunionConsultiva from "./ReunionConsultiva";
import InformeConsultivo from "./InformeConsultivo";
import ConstructorOferta from "./ConstructorOferta";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const authAxios = axios.create();
authAxios.interceptors.request.use((cfg) => {
  const token = localStorage.getItem("mobilsafe_token");
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

const ESTADOS = [
  { v: "", label: "Todos" },
  { v: "borrador", label: "Borrador" },
  { v: "en_curso", label: "En curso" },
  { v: "completada", label: "Completada" },
  { v: "convertida", label: "Convertida" },
];

export default function Reuniones({ onBack }) {
  const [reuniones, setReuniones] = useState([]);
  const [clientes, setClientes] = useState({});
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState("");
  const [q, setQ] = useState("");
  const [vista, setVista] = useState(null); // {tipo:'reunion'|'informe', reunion, cliente}

  useEffect(() => { cargar(); }, []);

  async function cargar() {
    setLoading(true);
    // Separar try/catch por petición — si clientes falla, las reuniones deben mostrarse igual
    try {
      const r = await authAxios.get(`${API}/reuniones`);
      setReuniones(r.data || []);
    } catch (e) {
      console.error("GET /api/reuniones falló:", e?.response?.status, e?.message);
      toast.error("Error cargando reuniones");
    }
    try {
      const c = await authAxios.get(`${API}/clientes`);
      const mapa = {};
      (c.data || []).forEach((cl) => { mapa[cl.id] = cl; });
      setClientes(mapa);
    } catch (e) {
      console.error("GET /api/clientes falló:", e?.response?.status, e?.message);
    }
    setLoading(false);
  }

  const kpis = useMemo(() => {
    const total = reuniones.length;
    const en_curso = reuniones.filter((r) => r.estado === "en_curso").length;
    const completadas = reuniones.filter((r) => r.estado === "completada" || r.estado === "convertida").length;
    const convertidas = reuniones.filter((r) => r.estado === "convertida").length;
    const ratio = completadas > 0 ? Math.round((convertidas / completadas) * 100) : 0;
    return { total, en_curso, completadas, convertidas, ratio };
  }, [reuniones]);

  const filtradas = useMemo(() => {
    let arr = [...reuniones];
    if (filtroEstado) arr = arr.filter((r) => r.estado === filtroEstado);
    if (q.trim()) {
      const s = q.toLowerCase();
      arr = arr.filter((r) => {
        const cl = clientes[r.cliente_id];
        return (cl?.empresa || "").toLowerCase().includes(s)
          || (cl?.cif || "").toLowerCase().includes(s);
      });
    }
    return arr;
  }, [reuniones, filtroEstado, q, clientes]);

  if (vista?.tipo === "reunion") {
    return <ReunionConsultiva reunionId={vista.reunion.id} cliente={vista.cliente}
      onBack={() => { setVista(null); cargar(); }}
      onAbrirInforme={(r) => setVista({ tipo: "informe", reunion: r, cliente: vista.cliente })} />;
  }
  if (vista?.tipo === "informe") {
    return <InformeConsultivo reunion={vista.reunion} cliente={vista.cliente}
      onBack={() => { setVista(null); cargar(); }} onUpdated={cargar}
      onCrearOferta={(nuevaOferta) => setVista({ tipo: "oferta", ofertaId: nuevaOferta.id, cliente: vista.cliente })} />;
  }
  if (vista?.tipo === "oferta") {
    return <ConstructorOferta ofertaId={vista.ofertaId} cliente={vista.cliente}
      onBack={() => { setVista(null); cargar(); }} />;
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="reuniones-page">
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-3">
          <button onClick={onBack} data-testid="reuniones-back"
            className="p-2 hover:bg-slate-100 rounded-lg">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex-1">
            <h1 className="text-xl font-bold flex items-center gap-2">
              <Compass className="w-5 h-5 text-emerald-600" /> Reuniones consultivas
            </h1>
            <p className="text-xs text-slate-500">Listado global del comercial · Paso 3 spec v3.2.A</p>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-6 space-y-5">
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Kpi testid="kpi-total" icon={<Users className="w-4 h-4" />} label="Total" value={kpis.total} />
          <Kpi testid="kpi-encurso" icon={<Clock className="w-4 h-4 text-amber-600" />} label="En curso" value={kpis.en_curso} />
          <Kpi testid="kpi-completadas" icon={<CheckCircle2 className="w-4 h-4 text-emerald-600" />} label="Completadas" value={kpis.completadas} />
          <Kpi testid="kpi-convertidas" icon={<CircleDashed className="w-4 h-4 text-blue-600" />} label="Convertidas a oferta" value={kpis.convertidas} />
          <Kpi testid="kpi-ratio" icon={<TrendingUp className="w-4 h-4 text-violet-600" />} label="Ratio conversión" value={`${kpis.ratio}%`} />
        </div>

        {/* Filtros */}
        <div className="bg-white rounded-xl border p-3 flex items-center gap-2 flex-wrap">
          <div className="flex items-center gap-2 flex-1 min-w-[200px]">
            <Search className="w-4 h-4 text-slate-400" />
            <input type="text" value={q} onChange={(e) => setQ(e.target.value)}
              data-testid="reuniones-buscar"
              placeholder="Buscar por cliente o CIF…"
              className="flex-1 px-2 py-1.5 text-sm focus:outline-none" />
          </div>
          <div className="flex items-center gap-1">
            {ESTADOS.map((e) => (
              <button key={e.v} onClick={() => setFiltroEstado(e.v)}
                data-testid={`filtro-estado-${e.v || "todos"}`}
                className={`px-3 py-1.5 text-xs rounded-md border transition ${
                  filtroEstado === e.v
                    ? "bg-emerald-600 text-white border-emerald-600"
                    : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
                }`}>
                {e.label}
              </button>
            ))}
          </div>
        </div>

        {/* Tabla */}
        <div className="bg-white rounded-xl border overflow-hidden">
          {loading ? (
            <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : filtradas.length === 0 ? (
            <div className="p-10 text-center text-slate-400 text-sm">
              {reuniones.length === 0 ? "Sin reuniones todavía. Crea una desde la ficha de un cliente." : "Sin resultados para este filtro."}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="text-left p-3">Cliente</th>
                  <th className="text-left p-3">Fecha</th>
                  <th className="text-center p-3">Progreso cuestionario</th>
                  <th className="text-center p-3">Estado</th>
                  <th className="text-left p-3">Oferta generada</th>
                  <th className="text-right p-3"></th>
                </tr>
              </thead>
              <tbody>
                {filtradas.map((r) => {
                  const cl = clientes[r.cliente_id];
                  const respuestas = (r.respuestas_cuestionario || []).length;
                  return (
                    <tr key={r.id} className="border-t hover:bg-slate-50" data-testid={`reunion-row-${r.id}`}>
                      <td className="p-3">
                        <div className="font-semibold">{cl?.empresa || <span className="text-slate-400 italic">cliente no disponible</span>}</div>
                        {cl?.cif && <div className="text-xs text-slate-500">{cl.cif}</div>}
                      </td>
                      <td className="p-3 text-slate-600">
                        {r.fecha_reunion
                          ? new Date(r.fecha_reunion).toLocaleDateString("es-ES")
                          : new Date(r.created_at).toLocaleDateString("es-ES")}
                      </td>
                      <td className="p-3 text-center">
                        <span className="text-xs font-semibold text-slate-700">{respuestas} / 26</span>
                        <div className="mt-1 h-1.5 bg-slate-100 rounded overflow-hidden w-24 mx-auto">
                          <div className="h-full bg-emerald-500" style={{ width: `${Math.min(100, (respuestas / 26) * 100)}%` }} />
                        </div>
                      </td>
                      <td className="p-3 text-center"><EstadoBadge estado={r.estado} /></td>
                      <td className="p-3 text-xs text-slate-500">
                        {r.oferta_id ? <span className="text-emerald-600 font-medium">✓ oferta #{r.oferta_id.slice(0, 6)}</span> : "—"}
                      </td>
                      <td className="p-3 text-right whitespace-nowrap">
                        <button onClick={() => setVista({ tipo: "reunion", reunion: r, cliente: cl })}
                          data-testid={`btn-continuar-${r.id}`}
                          className="text-xs px-2.5 py-1 bg-emerald-600 text-white rounded hover:bg-emerald-700 inline-flex items-center gap-1 mr-1">
                          <Play className="w-3 h-3" /> {r.estado === "completada" || r.estado === "convertida" ? "Ver" : "Continuar"}
                        </button>
                        {(r.estado === "completada" || r.estado === "convertida" || r.diagnostico_ia) && (
                          <button onClick={() => setVista({ tipo: "informe", reunion: r, cliente: cl })}
                            data-testid={`btn-informe-${r.id}`}
                            className="text-xs px-2.5 py-1 bg-violet-600 text-white rounded hover:bg-violet-700 inline-flex items-center gap-1">
                            <FileText className="w-3 h-3" /> Informe
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
}

function Kpi({ icon, label, value, testid }) {
  return (
    <div className="bg-white rounded-xl border p-3" data-testid={testid}>
      <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">{icon} {label}</div>
      <div className="text-2xl font-bold text-slate-900">{value}</div>
    </div>
  );
}

function EstadoBadge({ estado }) {
  const config = {
    borrador: { bg: "bg-slate-100", text: "text-slate-600", label: "Borrador" },
    en_curso: { bg: "bg-amber-100", text: "text-amber-700", label: "En curso" },
    completada: { bg: "bg-emerald-100", text: "text-emerald-700", label: "Completada" },
    convertida: { bg: "bg-blue-100", text: "text-blue-700", label: "Convertida" },
  }[estado] || { bg: "bg-slate-100", text: "text-slate-600", label: estado };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
}
